import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { api, errorMessage, unwrap, type S } from "../api/client";
import { useOrg, useOrgId } from "../api/org";
import { Alert, Button, Card, ChipsInput, Spinner, Switch, useToast } from "../components/ui";
import { useT } from "../i18n/I18nProvider";
import type { MessageKey } from "../i18n/keys";

const EMAIL = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

/** Each kind's name and meaning in the reader's language (the API's English `label` and `description` stand in for a
 * kind this list does not know yet). */
const KINDS: Record<S["NotificationKind"], { label: MessageKey; description: MessageKey }> = {
  leads: { label: "common.notifications.kind.leads", description: "common.notifications.kind.leadsDescription" },
  billing: { label: "common.notifications.kind.billing", description: "common.notifications.kind.billingDescription" },
  credits: { label: "common.notifications.kind.credits", description: "common.notifications.kind.creditsDescription" },
  usage: { label: "common.notifications.kind.usage", description: "common.notifications.kind.usageDescription" },
  website: { label: "common.notifications.kind.website", description: "common.notifications.kind.websiteDescription" },
  integrations: { label: "common.notifications.kind.integrations", description: "common.notifications.kind.integrationsDescription" },
};

function Row({ pref, canManage, onSave, saving }: {
  pref: S["EmailPreference"];
  canManage: boolean;
  saving: boolean;
  onSave: (write: S["EmailPreferenceWrite"]) => void;
}) {
  const t = useT();
  const [editing, setEditing] = useState(false);
  const [addresses, setAddresses] = useState<string[]>(pref.recipients);
  const to = pref.recipients.length ? pref.recipients : pref.default_recipients;
  const kind = KINDS[pref.kind];
  return (
    <div className="stage-item" style={{ gridTemplateColumns: "minmax(0, 1fr) auto" }}>
      <div style={{ minWidth: 0 }}>
        <b>{kind ? t(kind.label) : pref.label}</b>
        <div className="small muted" style={{ marginTop: 2 }}>{kind ? t(kind.description) : pref.description}</div>
        {pref.enabled && !editing && (
          <div className="small" style={{ marginTop: 6 }}>
            <span className="faint">{t("common.notifications.to")} </span>
            {to.length ? <span dir="auto">{to.join(", ")}</span> : <span className="faint">{t("common.notifications.nobody")}</span>}
            {!pref.recipients.length && <span className="faint"> {t("common.notifications.default")}</span>}
            {canManage && pref.custom_recipients && (
              <Button size="sm" variant="ghost" onClick={() => setEditing(true)} style={{ marginInlineStart: 6 }}>
                {t("common.change")}
              </Button>
            )}
          </div>
        )}
        {editing && (
          <div className="stack" style={{ gap: 8, marginTop: 8 }}>
            <ChipsInput
              values={addresses}
              onChange={setAddresses}
              placeholder={t("common.notifications.addressesPlaceholder")}
              normalize={(v) => v.trim().toLowerCase()}
              validate={(v) => (EMAIL.test(v) ? null : t("common.notifications.notAnEmail", { value: v }))}
            />
            <div className="row">
              <Button size="sm" loading={saving} onClick={() => { onSave({ kind: pref.kind, enabled: pref.enabled, recipients: addresses }); setEditing(false); }}>
                {t("common.save")}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => { setAddresses(pref.recipients); setEditing(false); }}>
                {t("common.cancel")}
              </Button>
              <span className="small faint">{t("common.notifications.emptyDefault")}</span>
            </div>
          </div>
        )}
      </div>
      <Switch
        checked={pref.enabled}
        disabled={!canManage || saving}
        onChange={(on) => onSave({ kind: pref.kind, enabled: on, recipients: pref.custom_recipients ? pref.recipients : null })}
      />
    </div>
  );
}

/** Settings › Notifications: which notification emails the company receives, and where. */
export function NotificationSettings() {
  const t = useT();
  const orgId = useOrgId();
  const { can } = useOrg();
  const canManage = can("settings_rules", "edit");
  const qc = useQueryClient();
  const toast = useToast();
  const key = ["email-preferences", orgId];
  const prefs = useQuery({
    queryKey: key,
    queryFn: () => unwrap(api.GET("/v1/orgs/{org_id}/email-preferences", { params: { path: { org_id: orgId } } })),
  });
  const save = useMutation({
    mutationFn: (item: S["EmailPreferenceWrite"]) =>
      unwrap(api.PUT("/v1/orgs/{org_id}/email-preferences", { params: { path: { org_id: orgId } }, body: { items: [item] } })),
    onSuccess: (data) => {
      qc.setQueryData(key, data);
      toast(t("common.notifications.saved"));
    },
    onError: (e) => toast(errorMessage(e), true),
  });
  return (
    <Card title={t("common.notifications.title")} description={t("common.notifications.description")} pad={false}>
      {prefs.isLoading ? (
        <Spinner />
      ) : prefs.error ? (
        <div className="card-body"><Alert tone="danger">{errorMessage(prefs.error)}</Alert></div>
      ) : (
        <div className="stage-list">
          {prefs.data!.items.map((p) => (
            <Row key={`${p.kind}:${p.recipients.join(",")}`} pref={p} canManage={canManage} saving={save.isPending} onSave={(w) => save.mutate(w)} />
          ))}
        </div>
      )}
    </Card>
  );
}
